import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../services/api/analyticsService';
import { getTradeTheme } from '../../constants/tradeTheme';
import { SEARCH_TRADE_IDS } from '../../constants/serviceSearchCatalog';
import {
  buildResultsPath,
  buildSearchEntryPath,
  buildSomethingElsePath,
  buildSomethingElseWizardState,
  buildTaskStepPath,
  buildTimingStepPath,
  getSearchTaskGroups,
  getSearchTaskLabel,
  getSearchTimingOptions,
  getSearchTradeLabel,
  isSearchTrade,
  readServiceSearchParams,
  SERVICE_SEARCH_PATH,
} from '../../utils/serviceSearch';
import ServiceSearchBar from '../ServiceSearchBar/ServiceSearchBar';
import EmptyMatchWaitlist from '../EmptyMatchWaitlist/EmptyMatchWaitlist';
import SEO from '../common/SEO/SEO';
import { buildServiceSearchSeo, buildTradeSeo } from '../common/SEO/seoHelpers';
import {
  AnswerChip,
  AnswerChipRow,
  BackButton,
  DescribeField,
  FlowBody,
  FlowCard,
  FlowHeader,
  FlowHeaderRow,
  FlowPage,
  NoteText,
  OptionButton,
  OptionGrid,
  OptionGroup,
  OptionGroupTitle,
  OptionIcon,
  ProgressFill,
  ProgressLabel,
  ProgressTrack,
  QuestionHint,
  QuestionTitle,
  TaskButton,
  TaskGrid,
} from './ServiceSearchFlow.styled';

const SOMETHING_ELSE_COLOR = '#475569';

/**
 * Honest progress for the current path. Results are the final screen, so the bar never reads 100%
 * while a question is still open.
 * @param {'trade' | 'task' | 'timing'} step
 * @param {string} from `trade` (browse path), `search` (typeahead), or '' (tile / deep link)
 */
function progressFor(step, from) {
  if (step === 'trade') return { current: 1, total: 3 };
  if (step === 'task') return from === 'trade' ? { current: 2, total: 3 } : { current: 1, total: 2 };
  if (from === 'trade') return { current: 3, total: 3 };
  if (from === 'search') return { current: 1, total: 1 };
  return { current: 2, total: 2 };
}

function FlowShell({ step, from, onBack, answers = [], wide = false, tradeColor, children }) {
  const progress = step ? progressFor(step, from) : null;
  return (
    <FlowPage>
      <FlowCard $wide={wide} style={tradeColor ? { '--trade': tradeColor } : undefined}>
        <FlowHeader>
          <FlowHeaderRow>
            <BackButton onClick={onBack}>
              <ArrowBackRoundedIcon />
              Back
            </BackButton>
            {progress ? (
              <ProgressLabel>
                {progress.total === 1
                  ? 'Last question'
                  : `Question ${progress.current} of ${progress.total}`}
              </ProgressLabel>
            ) : null}
          </FlowHeaderRow>
          {progress ? (
            <ProgressTrack
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuenow={progress.current - 1}
              aria-label="Search progress"
            >
              <ProgressFill $percent={(progress.current / (progress.total + 1)) * 100} />
            </ProgressTrack>
          ) : null}
          {answers.length > 0 ? (
            <AnswerChipRow>
              {answers.map((answer) => (
                <AnswerChip
                  key={answer.key}
                  onClick={answer.onEdit}
                  aria-label={`Change ${answer.kind}: ${answer.label}`}
                >
                  {answer.label}
                  <EditRoundedIcon aria-hidden />
                </AnswerChip>
              ))}
            </AnswerChipRow>
          ) : null}
        </FlowHeader>
        <FlowBody>{children}</FlowBody>
      </FlowCard>
    </FlowPage>
  );
}

FlowShell.propTypes = {
  step: PropTypes.oneOf(['trade', 'task', 'timing']),
  from: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      kind: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onEdit: PropTypes.func.isRequired,
    }),
  ),
  /** Wider card for the task step's 4-column grid. */
  wide: PropTypes.bool,
  /** Trade color for header, progress and hover tints; brand blue when omitted. */
  tradeColor: PropTypes.string,
  children: PropTypes.node,
};

function NoteReminder({ note }) {
  if (!note) return null;
  return (
    <NoteText>
      You searched for <strong>“{note}”</strong>. We&apos;ll add it to your request as a note.
    </NoteText>
  );
}

NoteReminder.propTypes = { note: PropTypes.string };

/**
 * Homeowner service search: search bar entry, trade picker, task (sub-service), and timing.
 * State lives in the URL so answers stay editable and the back button works.
 *
 * Routes: `/find-a-pro` (entry, `?step=trade`, `?step=other`) and `/{trade}` (task, then `?task=` timing).
 * @param {{ serviceType?: string }} props
 */
const ServiceSearchFlow = ({ serviceType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const step = serviceType ? null : searchParams.get('step');
  const search = useMemo(
    () => (serviceType ? readServiceSearchParams(serviceType, searchParams) : null),
    [serviceType, searchParams],
  );
  const note = (search?.note ?? searchParams.get('note') ?? '').trim();
  const [description, setDescription] = useState(note);
  const somethingElseWizardState = useMemo(
    () => buildSomethingElseWizardState({ description }),
    [description],
  );

  const goBack = (fallbackPath) => {
    if (location.key !== 'default') navigate(-1);
    else navigate(fallbackPath);
  };

  // --- /find-a-pro ---------------------------------------------------------
  if (!serviceType) {
    if (step === 'trade') {
      return (
        <>
          <SEO {...buildServiceSearchSeo()} />
          <FlowShell step="trade" onBack={() => goBack(buildSearchEntryPath({ note }))}>
            <QuestionTitle>Which kind of pro do you need?</QuestionTitle>
            <QuestionHint>Pick the closest match. You can change it later.</QuestionHint>
            <NoteReminder note={note} />
            <OptionGrid>
              {SEARCH_TRADE_IDS.map((tradeId) => {
                const theme = getTradeTheme(tradeId);
                const Icon = theme.icon;
                return (
                  <OptionButton
                    key={tradeId}
                    onClick={() => navigate(buildTaskStepPath(tradeId, { note, from: 'trade' }))}
                  >
                    <OptionIcon $color={theme.color} aria-hidden>
                      <Icon />
                    </OptionIcon>
                    {getSearchTradeLabel(tradeId)}
                  </OptionButton>
                );
              })}
              <OptionButton $muted onClick={() => navigate(buildSomethingElsePath({ note }))}>
                <OptionIcon $color={SOMETHING_ELSE_COLOR} aria-hidden>
                  <MoreHorizRoundedIcon />
                </OptionIcon>
                Something else
              </OptionButton>
            </OptionGrid>
          </FlowShell>
        </>
      );
    }

    if (step === 'other') {
      return (
        <>
          <SEO {...buildServiceSearchSeo()} />
          <FlowShell onBack={() => goBack(buildSearchEntryPath({ note }))}>
            <QuestionTitle>Tell us what you need</QuestionTitle>
            <QuestionHint>
              We don&apos;t list this kind of pro yet. Describe the job and our team will look for
              a trusted local pro and reach out.
            </QuestionHint>
            <EmptyMatchWaitlist
              tradeLabel=""
              headline="Where should we reach you?"
              subcopy="We only use your details to follow up on this request."
              wizardState={somethingElseWizardState}
              user={user}
              getSubmitError={() =>
                description.trim() ? '' : 'Describe what you need before sending.'
              }
            >
              <DescribeField
                label="What do you need help with?"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                required
              />
            </EmptyMatchWaitlist>
          </FlowShell>
        </>
      );
    }

    return (
      <>
        <SEO {...buildServiceSearchSeo()} />
        <FlowPage>
          <ServiceSearchBar initialQuery={note} headingLevel="h1" />
        </FlowPage>
      </>
    );
  }

  // --- /{trade} ------------------------------------------------------------
  if (!isSearchTrade(serviceType)) {
    return <Navigate to={SERVICE_SEARCH_PATH} replace />;
  }

  const tradeLabel = getSearchTradeLabel(serviceType);
  const tradeColor = getTradeTheme(serviceType).color;
  const tradeSeo = <SEO {...buildTradeSeo(serviceType)} />;
  const { hasTaskAnswer, task, from } = search;
  const goToResults = (timingId) => {
    trackEvent({
      eventType: 'service_search',
      businessId: 'app',
      serviceShortName: serviceType,
      ...(task ? { subserviceShortName: task } : {}),
      searchChannel: 'wizard',
    });
    navigate(buildResultsPath(serviceType, { task, timing: timingId, note }));
  };
  const tradeAnswer = {
    key: 'trade',
    kind: 'trade',
    label: tradeLabel,
    onEdit: () => navigate(buildSearchEntryPath({ note })),
  };

  if (!hasTaskAnswer) {
    const groups = getSearchTaskGroups(serviceType);
    return (
      <>
        {tradeSeo}
        <FlowShell
          step="task"
          from={from}
          onBack={() => goBack(SERVICE_SEARCH_PATH)}
          answers={[tradeAnswer]}
          wide
          tradeColor={tradeColor}
        >
          <QuestionTitle>What do you need help with?</QuestionTitle>
          <QuestionHint>Choose the closest task so we can show the right {tradeLabel} pros.</QuestionHint>
          <NoteReminder note={note} />
          {groups.map((group) => (
            <OptionGroup key={group.id}>
              {groups.length > 1 ? <OptionGroupTitle>{group.name}</OptionGroupTitle> : null}
              <TaskGrid>
                {group.options.map((option) => (
                  <TaskButton
                    key={option.slug}
                    title={option.fullLabel}
                    onClick={() =>
                      navigate(buildTimingStepPath(serviceType, option.slug, { note, from }))
                    }
                  >
                    {option.label}
                  </TaskButton>
                ))}
              </TaskGrid>
            </OptionGroup>
          ))}
          <OptionGroup>
            <OptionGrid>
              <OptionButton
                $muted
                onClick={() => navigate(buildTimingStepPath(serviceType, null, { note, from }))}
              >
                <OptionIcon aria-hidden>
                  <HelpOutlineRoundedIcon />
                </OptionIcon>
                Not sure / show all {tradeLabel} pros
              </OptionButton>
            </OptionGrid>
          </OptionGroup>
        </FlowShell>
      </>
    );
  }

  const taskLabel = task ? getSearchTaskLabel(serviceType, task) : `All ${tradeLabel} pros`;
  return (
    <>
      {tradeSeo}
      <FlowShell
        step="timing"
        tradeColor={tradeColor}
        from={from}
        onBack={() => goBack(buildTaskStepPath(serviceType, { note, from }))}
        answers={[
          tradeAnswer,
          {
            key: 'task',
            kind: 'task',
            label: taskLabel,
            onEdit: () => navigate(buildTaskStepPath(serviceType, { note, from })),
          },
        ]}
      >
        <QuestionTitle>When do you need help?</QuestionTitle>
        <QuestionHint>We&apos;ll show pros whose availability fits.</QuestionHint>
        <OptionGrid>
          {getSearchTimingOptions().map((option) => {
            const Icon = option.icon;
            return (
              <OptionButton
                key={option.id}
                onClick={() => goToResults(option.id)}
              >
                <OptionIcon aria-hidden>
                  <Icon />
                </OptionIcon>
                {option.searchLabel}
              </OptionButton>
            );
          })}
        </OptionGrid>
      </FlowShell>
    </>
  );
};

ServiceSearchFlow.propTypes = {
  /** Trade from the route (`/plumbing`, …); omitted on `/find-a-pro`. */
  serviceType: PropTypes.string,
};

export default ServiceSearchFlow;
